import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Search, Plus, X } from 'lucide-react';
import { permissionsService } from '@services/permissions.service';
import type { Permission } from '@apptypes/index';
import { cn } from '@lib/utils';
import { PageGuard } from '@components/PageGuard';
import { PermissionGate } from '@components/PermissionGate';

const scopeColors: Record<string, string> = {
  all: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  chekit_only: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
  business_only: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
};

function CreatePermissionModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState('all');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () => permissionsService.create({ code: code.trim(), name: name.trim(), scope, description: description.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)] backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-soft w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h2 className="font-semibold text-[var(--text-primary)]">Create Permission</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Code *
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. orders.export"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] font-mono text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
            <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
              Format: module.action (e.g. orders.read, businesses.approve)
            </p>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Export Orders"
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Scope
            </label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
            >
              <option value="all">all: available at any scope</option>
              <option value="chekit_only">chekit_only: internal staff only</option>
              <option value="business_only">business_only: business context only</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this permission allow?"
              rows={2}
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
            />
          </div>

          {mutation.error && (
            <p className="text-[13px] text-[var(--state-danger)]">
              {(mutation.error as any)?.message ?? 'Something went wrong.'}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--border-subtle)]">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] transition-all duration-200 hover:border-[var(--border-strong)]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!code.trim() || !name.trim() || mutation.isPending}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {mutation.isPending ? 'Creating...' : 'Create Permission'}
          </button>
        </div>
      </div>
    </div>
  );
}

export const PermissionsBrowserPage = () => {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const { data: permissions, isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsService.list(),
  });

  const getModule = (perm: Permission) => perm.code.split('.')[0] ?? 'other';

  const grouped = (permissions || []).reduce<Record<string, Permission[]>>((acc, perm) => {
    const mod = getModule(perm);
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(perm);
    return acc;
  }, {});

  const filteredModules = Object.entries(grouped).filter(([mod, perms]) =>
    !search ||
    mod.toLowerCase().includes(search.toLowerCase()) ||
    perms.some((p) => p.code.toLowerCase().includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <PageGuard permission="permissions.read">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-1">
              System
            </p>
            <h2 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)] leading-none">
              Permissions
            </h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-1">
              {permissions?.length || 0} permissions across {Object.keys(grouped).length} modules
            </p>
          </div>
          <PermissionGate permission="permissions.assign">
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 disabled:opacity-50 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Create Permission
            </button>
          </PermissionGate>
        </div>

        {/* Search bar */}
        <div className="relative max-w-md">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
            strokeWidth={1.5}
          />
          <input
            type="text"
            placeholder="Search permissions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
          />
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="rounded-3xl bg-white shadow-soft p-12 text-center text-[14px] text-[var(--text-muted)]">
            Loading permissions...
          </div>
        )}

        {/* Module groups */}
        <div className="space-y-4">
          {filteredModules.sort(([a], [b]) => a.localeCompare(b)).map(([mod, perms]) => (
            <div key={mod} className="rounded-3xl bg-white shadow-soft overflow-hidden">
              {/* Module header */}
              <div className="px-6 py-3 bg-[var(--bg-base)] border-b border-[var(--border-subtle)]">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] flex items-center gap-2">
                  <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {mod}
                  <span className="normal-case tracking-normal font-normal text-[var(--text-muted)]">
                    ({perms.length})
                  </span>
                </h3>
              </div>

              {/* Permission rows */}
              <div className="divide-y divide-[var(--border-subtle)]">
                {perms.map((perm) => (
                  <div
                    key={perm.permissionId}
                    className="flex items-center justify-between px-6 py-3 hover:bg-[var(--bg-base)] transition-colors"
                  >
                    <div>
                      <code className="text-[13px] font-mono text-[var(--text-primary)]">{perm.code}</code>
                      <div className="text-[12px] text-[var(--text-muted)] mt-0.5">{perm.name}</div>
                    </div>
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                        scopeColors[perm.scope] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]'
                      )}
                    >
                      {perm.scope}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {showCreate && <CreatePermissionModal onClose={() => setShowCreate(false)} />}
      </div>
    </PageGuard>
  );
};
